import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { BusinessCenter, Sync } from '@mui/icons-material';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Stack,
    Button,
} from '@mui/material';
import { LoadingSpinnerPresets } from 'dg-web-shared/common/components/material-ui/PresetLoadingSpinner.tsx';
import { useServerFetch } from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import { Localized } from 'dg-web-shared/lib/Localization.tsx';
import { EmDash } from 'dg-web-shared/lib/Punctuation.ts';
import { VatRateType } from 'dg-web-shared/model/VatAccount.tsx';
import { OperatorAsyncLoadedSection } from '../../app/components/OperatorAsyncLoadedSection.tsx';
import { languageTexts } from '../../common/i18n/GeneralTexts.ts';
import { OrganizationAddress } from '../../common/state/OperatorDataState.ts';
import { HeaderWithButtons } from '../../shared-mui-components/HeaderWithButtons.tsx';
import { MainContainer } from '../../shared-mui-components/MainContent.tsx';
import { SectionCard } from '../../shared-mui-components/SectionCard.tsx';
import { ReadOnlyTextField } from '../../ui/material-ui/ReadOnlyTextField.tsx';
import { SyncAppModal } from '../../sync-app/components/SyncAppModal.tsx';
import { useOperatorContext } from '../../app/components/BaseLayoutAndData.tsx';

interface InvoiceAddress {
    name1: string;
    name2: string | null;
    street: string | null;
    zipCode: string | null;
    city: string | null;
}

interface PaymentGroupDTO {
    dtaAccountId: number;
    dtaAccountDescription: string;
    dtaAccountIban: string;
    paymentReason: string | null;
    vatNumber: string | null;
    vatNumberOwner: string | null;
    vatRateType: VatRateType | null;
    vatRatePercent: number | null;
}

interface OperatorMasterDataDTO {
    operatorName: string;
    operator: OrganizationAddress;
    language: string;
    contractPartner: OrganizationAddress | null;
    owner: OrganizationAddress | null;
    shipping: InvoiceAddress | null;
    operatorApiKey: string | null;
    anonymizeAfterDays: number;
    paymentGroups: PaymentGroupDTO[];
}

export function OperatorMasterData() {
    const { currentLogin, operatorExternalId } = useOperatorContext();

    if (!currentLogin.permissions.masterDataAndStatistics) {
        return <Navigate to={`/${operatorExternalId}/profile`} replace />;
    }
    return <OperatorMasterDataPage />;
}

function OperatorMasterDataPage() {
    const { operatorData, currentLogin } = useOperatorContext();
    const [syncAppOpen, setSyncAppOpen] = useState(false);
    const showSyncApp =
        operatorData.hasTaxomexModule && currentLogin.permissions.syncApp;
    const [masterDataState] = useServerFetch<OperatorMasterDataDTO, object>(
        () => ({
            url: '/ui-api/operator-account/operator-master-data',
        }),
        {},
    );

    return (
        <MainContainer>
            <OperatorAsyncLoadedSection
                requestState={masterDataState}
                pendingLoaderPreset={
                    LoadingSpinnerPresets.FillAllSpaceAndCenter
                }
                render={masterData => (
                    <Stack sx={{ gap: '28px' }}>
                        <Box sx={{ backgroundColor: 'common.white' }}>
                            <HeaderWithButtons
                                title={masterData.operatorName}
                                icon={BusinessCenter}
                                headerButtons={
                                    showSyncApp ? (
                                        <Button
                                            variant="contained"
                                            startIcon={<Sync />}
                                            onClick={() => setSyncAppOpen(true)}
                                        >
                                            Sync App
                                        </Button>
                                    ) : undefined
                                }
                            />
                        </Box>
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: '1fr 1fr',
                                gap: '28px',
                                alignItems: 'start',
                            }}
                        >
                            <Stack sx={{ gap: '28px' }}>
                                <MasterDataCard masterData={masterData} />
                                {masterData.paymentGroups.length === 1 && (
                                    <SinglePaymentGroupCard
                                        paymentGroup={
                                            masterData.paymentGroups[0]
                                        }
                                    />
                                )}
                            </Stack>
                            <Stack sx={{ gap: '28px' }}>
                                {masterData.operatorApiKey && (
                                    <IntegrationCard
                                        apiKey={masterData.operatorApiKey}
                                    />
                                )}
                                <PrivacyCard
                                    days={masterData.anonymizeAfterDays}
                                />
                            </Stack>
                        </Box>
                        {masterData.paymentGroups.length > 1 && (
                            <PaymentGroupsTableCard
                                paymentGroups={masterData.paymentGroups}
                            />
                        )}
                    </Stack>
                )}
            />
            {showSyncApp && (
                <SyncAppModal
                    open={syncAppOpen}
                    onClose={() => setSyncAppOpen(false)}
                />
            )}
        </MainContainer>
    );
}

function MasterDataCard({ masterData }: { masterData: OperatorMasterDataDTO }) {
    return (
        <SectionCard
            title={
                <Localized
                    de="Stammdaten"
                    fr="Données de base"
                    it="Dati di base"
                    en="Master data"
                />
            }
        >
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '28px',
                    alignItems: 'start',
                }}
            >
                <Stack sx={{ gap: '22px' }}>
                    <ReadOnlyTextField
                        label={
                            <Localized
                                de="Bezeichnung"
                                fr="Désignation"
                                it="Denominazione"
                                en="Denomination"
                            />
                        }
                        value={masterData.operatorName}
                    />
                    <ReadOnlyTextField
                        label={
                            <Localized
                                de="Betreiber"
                                fr="Exploitant"
                                it="Gestore"
                                en="Operator"
                            />
                        }
                        value={<AddressLines address={masterData.operator} />}
                    />
                    <ReadOnlyTextField
                        label={
                            <Localized
                                de="Korrespondenzsprache"
                                fr="Langue de correspondance"
                                it="Lingua di corrispondenza"
                                en="Correspondence language"
                            />
                        }
                        value={languageTexts[masterData.language]}
                    />
                </Stack>
                <Stack sx={{ gap: '22px' }}>
                    {masterData.contractPartner && (
                        <ReadOnlyTextField
                            label={
                                <Localized
                                    de="Vertragspartner"
                                    fr="Titulaire de contrat"
                                    it="Partner contrattuale"
                                    en="Contractual partner"
                                />
                            }
                            value={
                                <AddressLines
                                    address={masterData.contractPartner}
                                />
                            }
                        />
                    )}
                    {masterData.owner && (
                        <ReadOnlyTextField
                            label={
                                <Localized
                                    de="Eigentümer"
                                    fr="Propriétaire"
                                    it="Proprietario"
                                    en="Owner"
                                />
                            }
                            value={<AddressLines address={masterData.owner} />}
                        />
                    )}
                    {masterData.shipping && (
                        <ReadOnlyTextField
                            label={
                                <Localized
                                    de="Versandadresse"
                                    fr="Adresse d'envoi"
                                    it="Indirizzo d'invio"
                                    en="Mailing address"
                                />
                            }
                            value={
                                <AddressLines address={masterData.shipping} />
                            }
                        />
                    )}
                </Stack>
            </Box>
        </SectionCard>
    );
}

function AddressLines({
    address,
}: {
    address: OrganizationAddress | InvoiceAddress;
}) {
    return (
        <>
            {address.name1 && <div>{address.name1}</div>}
            {address.name2 && <div>{address.name2}</div>}
            {address.street && <div>{address.street}</div>}
            {(address.zipCode || address.city) && (
                <div>
                    {[address.zipCode, address.city].filter(Boolean).join(' ')}
                </div>
            )}
        </>
    );
}

const paymentCriteriaTitle = (
    <Localized
        de="Zahlungskriterien"
        fr="Modalité de paiement"
        it="Modalità di pagamento"
        en="Method of payment"
    />
);

const ibanLabel = <Localized de="IBAN" fr="IBAN" it="IBAN" en="IBAN" />;

const paymentReasonLabel = (
    <Localized
        de="Zahlungsgrund"
        fr="Motif du paiement"
        it="Motivo pagamento"
        en="Reason for payment"
    />
);

const vatLabel = <Localized de="MWST" fr="TVA" it="IVA" en="VAT" />;

function SinglePaymentGroupCard({
    paymentGroup,
}: {
    paymentGroup: PaymentGroupDTO;
}) {
    return (
        <SectionCard title={paymentCriteriaTitle}>
            <ReadOnlyTextField
                label={paymentReasonLabel}
                value={paymentGroup.paymentReason ?? EmDash}
            />
            <ReadOnlyTextField
                label={ibanLabel}
                value={paymentGroup.dtaAccountIban}
            />
            <ReadOnlyTextField
                label={vatLabel}
                value={<VatLines paymentGroup={paymentGroup} />}
            />
        </SectionCard>
    );
}

function PaymentGroupsTableCard({
    paymentGroups,
}: {
    paymentGroups: PaymentGroupDTO[];
}) {
    return (
        <SectionCard title={paymentCriteriaTitle} disableContentPadding>
            <Table sx={{ '& .MuiTableCell-body': { fontSize: '16px' } }}>
                <TableHead>
                    <TableRow>
                        <TableCell>
                            <Localized
                                de="Gruppe"
                                fr="Groupe"
                                it="Gruppo"
                                en="Group"
                            />
                        </TableCell>
                        <TableCell>{ibanLabel}</TableCell>
                        <TableCell>{paymentReasonLabel}</TableCell>
                        <TableCell>{vatLabel}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {paymentGroups.map(paymentGroup => (
                        <TableRow key={paymentGroup.dtaAccountId}>
                            <TableCell>
                                {paymentGroup.dtaAccountDescription}
                            </TableCell>
                            <TableCell>{paymentGroup.dtaAccountIban}</TableCell>
                            <TableCell>
                                {paymentGroup.paymentReason ?? EmDash}
                            </TableCell>
                            <TableCell>
                                <VatLines paymentGroup={paymentGroup} />
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </SectionCard>
    );
}

function VatLines({ paymentGroup }: { paymentGroup: PaymentGroupDTO }) {
    if (paymentGroup.vatRateType === null) {
        return EmDash;
    }
    return (
        <>
            <div>
                {paymentGroup.vatRateType === VatRateType.FREE ? (
                    <Localized de="Frei" fr="Exempt" it="Esente" en="Free" />
                ) : (
                    `${paymentGroup.vatNumber} (${paymentGroup.vatRatePercent}%)`
                )}
            </div>
            {paymentGroup.vatNumberOwner && (
                <div>{paymentGroup.vatNumberOwner}</div>
            )}
        </>
    );
}

function IntegrationCard({ apiKey }: { apiKey: string }) {
    return (
        <SectionCard
            title={
                <Localized
                    de="Integration"
                    fr="Intégration"
                    it="Integrazione"
                    en="Integration"
                />
            }
        >
            <ReadOnlyTextField
                label={
                    <Localized
                        de="API Key"
                        fr="API Key"
                        it="API Key"
                        en="API Key"
                    />
                }
                value={apiKey}
                sx={{
                    fontFamily: 'monospace',
                    letterSpacing: '0.04em',
                    wordBreak: 'break-all',
                }}
            />
        </SectionCard>
    );
}

function PrivacyCard({ days }: { days: number }) {
    return (
        <SectionCard
            title={
                <Localized
                    de="Datenschutz"
                    fr="Protection des données"
                    it="Protezione dei dati"
                    en="Data protection"
                />
            }
        >
            <ReadOnlyTextField
                label={
                    <Localized
                        de="Anonymisierung der Parkvorgänge nach"
                        fr="Anonymiser les procédures plus anciennes que"
                        it="Rendere anonime le procedure più vecchie di"
                        en="Anonymize parking transactions after"
                    />
                }
                value={
                    <Localized
                        de={`${days} Tagen`}
                        fr={`${days} jours`}
                        it={`${days} giorni`}
                        en={`${days} days`}
                    />
                }
            />
        </SectionCard>
    );
}
